import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AlertFloating } from "@/components/application/alerts/alerts";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
  useIltEnabled,
  useLearningPathsEnabled,
  usePaymentsEnabled,
} from "@/hooks/useFeatureFlags";
import {
  CourseDataSection,
  LearningPathDataSection,
  MetricCard,
  SalesRevenueSection,
  UserDataSection,
  useAnalyticsData,
  useSalesReportData,
  type SalesReportDateRange,
} from "@/features/admin/components/analytics";

export const Route = createFileRoute("/admin/analytics")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { data, isLoading, isError, refetch } = useAnalyticsData();

  // Sales reporting only exists while the payments feature flag is on (the API 404s it otherwise).
  const paymentsEnabled = usePaymentsEnabled();
  const [salesRange, setSalesRange] = useState<SalesReportDateRange>({});
  const sales = useSalesReportData(salesRange, { enabled: paymentsEnabled });
  // The API returns path stats either way; the section shows only while the learning-paths flag is on.
  const learningPathsEnabled = useLearningPathsEnabled();
  // LMS-138: ILT course feedback shows alongside the online course evaluation while ilt is on.
  const iltEnabled = useIltEnabled();

  return (
    <ProtectedRoute requiredPermissions={["Analytics.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout>
        <div className="bg-arc-base-white py-24">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-4 sm:px-6 lg:px-8">
            {/* Page Header */}
            <h1 className="font-heading text-arc-base-black text-4xl leading-[44px] font-semibold tracking-[-0.72px]">
              Analytics
            </h1>

            {/* Loading State */}
            {isLoading && (
              <div className="flex min-h-[400px] items-center justify-center">
                <LoadingIndicator size="lg" label="Loading analytics data..." />
              </div>
            )}

            {/* Error State */}
            {isError && (
              <AlertFloating
                title="Unable to load analytics"
                description="There was a problem loading the analytics data. Please try again later."
                confirmLabel="Retry"
                color="error"
                onConfirm={() => refetch()}
              />
            )}

            {/* Content - only show when not loading and no error */}
            {!isLoading && !isError && (
              <>
                {/* Quick View Section */}
                <section className="border-arc-gray-200 flex flex-col gap-8 border-b pb-12">
                  <h2 className="font-heading text-arc-base-black text-2xl leading-8 font-semibold">
                    Quick view
                  </h2>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                    <MetricCard
                      value={data.quickView.newUsersThisWeek}
                      label="New users this week"
                    />
                    <MetricCard value={data.quickView.totalUsers} label="Total users" />
                    <MetricCard value={data.quickView.coursesStarted} label="Courses started" />
                    <MetricCard value={data.quickView.coursesCompleted} label="Courses completed" />
                  </div>
                </section>

                {/* User Data Section */}
                <UserDataSection
                  usersBySector={data.usersBySector}
                  usersByLocalAuthority={data.usersByLocalAuthority}
                />

                {/* Course Data Section */}
                <CourseDataSection
                  courseCompletion={data.courseCompletion}
                  courseEvaluation={data.courseEvaluation}
                  iltFeedback={iltEnabled ? data.iltFeedback : undefined}
                />

                {/* Learning Path Data Section */}
                {learningPathsEnabled && (
                  <LearningPathDataSection learningPaths={data.learningPaths} />
                )}

                {/* Sales and Revenue Section */}
                {paymentsEnabled && (
                  <SalesRevenueSection
                    data={sales.data}
                    startDate={salesRange.startDate}
                    endDate={salesRange.endDate}
                    onStartDateChange={(startDate) =>
                      setSalesRange((range) => ({ ...range, startDate }))
                    }
                    onEndDateChange={(endDate) => setSalesRange((range) => ({ ...range, endDate }))}
                    onClearRange={() => setSalesRange({})}
                    isLoading={sales.isLoading}
                    isError={sales.isError}
                    onRetry={sales.refetch}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
